import { useMemo, useState, useCallback, useEffect, useRef } from "react"
import { Box, Checkbox, Table, TableBody, TableCell, TableContainer, TableRow, Divider, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"

const checkboxSx = { p: 0, mr: 0.5, "& .MuiSvgIcon-root": { fontSize: "1.1rem" } }

export function getVehicleKey(event) {
    return event?.vehicleSubtype?.name
        ? `sub:${event.vehicleSubtype.name}`
        : event?.vehicleType?.name
            ? `type:${event.vehicleType.name}`
            : "unknown"
}

export default function VehicleStats({ events = [], onFilterChange, initialFilter }) {
    const { t } = useTranslation()

    const [checkedRows, setCheckedRows] = useState(() => {
        if (Array.isArray(initialFilter) && initialFilter.length > 0) return new Set(initialFilter)
        const keys = new Set()
        for (const elem of events) keys.add(getVehicleKey(elem))
        return keys
    })

    const { rows, totals } = useMemo(() => {
        const counts = new Map() // key -> { key, label, entry, exit }

        const getLabel = (event) => {
            const subtype = event?.vehicleSubtype?.name
            if (subtype) return t(`pages.access.vehicle.subtypes.${subtype}`, subtype)

            const type = event?.vehicleType?.name
            if (type) return t(`pages.access.vehicle.types.${type}`, type)

            return t("pages.access.stats.unknown")
        }

        for (const elem of events) {
            const key = getVehicleKey(elem)
            const item = counts.get(key) ?? { key, label: getLabel(elem), entry: 0, exit: 0 }
            const direction = elem?.direction

            if (direction === "entry") item.entry += 1
            else if (direction === "exit") item.exit += 1
            else item.entry += 1 // fallback: unknown direction -> counts once

            counts.set(key, item)
        }

        const computedRows = Array.from(counts.values())
            .sort((a, b) => {
                const at = a.entry + a.exit
                const bt = b.entry + b.exit
                return bt - at || a.label.localeCompare(b.label)
            })

        const totals = computedRows.reduce(
            (acc, current) => ({
                entry: acc.entry + current.entry,
                exit: acc.exit + current.exit
            }),
            { entry: 0, exit: 0 }
        )

        return { rows: computedRows, totals }
    }, [events, t])

    // on mount: restore from initialFilter if valid; on date/data change: reset to all
    const mountedRef = useRef(false)
    useEffect(() => {
        if (!mountedRef.current) {
            mountedRef.current = true
            if (Array.isArray(initialFilter) && initialFilter.length > 0) {
                const allKeys = new Set(rows.map(elem => elem.key))
                const restored = new Set(initialFilter.filter(elem => allKeys.has(elem)))
                if (restored.size > 0) {
                    setCheckedRows(restored)
                    return
                }
            }
        }
        setCheckedRows(new Set(rows.map(elem => elem.key)))
    }, [rows]) // eslint-disable-line react-hooks/exhaustive-deps

    const allRowKeys = useMemo(() => new Set(rows.map(r => r.key)), [rows])
    const allRowsChecked = allRowKeys.size > 0 && allRowKeys.size === checkedRows.size && [...allRowKeys].every(k => checkedRows.has(k))

    // report filter changes to parent
    useEffect(() => {
        onFilterChange?.(checkedRows)
    }, [checkedRows]) // eslint-disable-line react-hooks/exhaustive-deps

    const toggleRow = useCallback((key) => {
        setCheckedRows(prev => {
            const next = new Set(prev)
            if (next.has(key)) {
                next.delete(key)
            } else {
                next.add(key)
            }
            return next
        })
    }, [])

    const toggleTotal = useCallback(() => {
        if (allRowsChecked) {
            setCheckedRows(new Set()) // deselect all
        } else {
            setCheckedRows(new Set(allRowKeys)) // select all
        }
    }, [allRowsChecked, allRowKeys])

    return (
        <Box sx={{ mt: 2 }}>

            <Divider sx={{ userSelect: "none" }}>
                <Typography>
                    {t("pages.access.vehicle.title")}
                </Typography>
            </Divider>

            <TableContainer
                sx={{ mt: 1 }}
                className="thin-scrollbar"
            >
                <Table size="small" sx={{ userSelect: "none" }}>
                    <TableBody>

                        <TableRow sx={{ "& td": { fontWeight: 600 } }}>
                            <TableCell sx={{ pl: "2px", pr: 0 }} />
                            <TableCell align="right" sx={{ px: 0 }}>{t("pages.access.entry_short")}</TableCell>
                            <TableCell align="right" sx={{ px: 0 }}>{t("pages.access.exit_short")}</TableCell>
                        </TableRow>

                        <TableRow sx={{ "& td": { fontWeight: 600 } }}>
                            <TableCell
                                sx={{
                                    pl: "2px",
                                    pr: 0,
                                    whiteSpace: "nowrap",
                                    cursor: "pointer"
                                }}
                                onClick={toggleTotal}
                            >
                                <Checkbox
                                    size="small"
                                    sx={checkboxSx}
                                    checked={allRowsChecked}
                                    tabIndex={-1}
                                />
                                {t("download.total")}
                            </TableCell>
                            <TableCell align="right" sx={{ pr: "4px", pl: 0 }}>{totals.entry}</TableCell>
                            <TableCell align="right" sx={{ pr: "4px", pl: 0 }}>{totals.exit}</TableCell>
                        </TableRow>

                        {rows.map((elem) => (
                            <TableRow key={elem.key} sx={{ "&:last-child td": { border: 0 } }}>
                                <TableCell
                                    sx={{
                                        pl: "2px",
                                        pr: 0,
                                        whiteSpace: "nowrap",
                                        cursor: "pointer"
                                    }}
                                    onClick={() => toggleRow(elem.key)}
                                >
                                    <Checkbox
                                        size="small"
                                        sx={checkboxSx}
                                        checked={checkedRows.has(elem.key)}
                                        tabIndex={-1}
                                    />
                                    {elem.label}
                                </TableCell>
                                <TableCell align="right" sx={{ pr: "4px", pl: 0 }}>{elem.entry}</TableCell>
                                <TableCell align="right" sx={{ pr: "4px", pl: 0 }}>{elem.exit}</TableCell>
                            </TableRow>
                        ))}

                    </TableBody>
                </Table>
            </TableContainer>

        </Box>
    )
}